<template>
  <div>

    <!-- Storage Metrics Overview -->
    <b-row class="mb-1">
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ totalStorages }}
              </h5>
              <small class="text-muted">Gesamt Lager</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                  class="text-success"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ rentedStorages }}
              </h5>
              <small class="text-muted">Vermietet</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="CircleIcon"
                  size="24"
                  class="text-warning"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ freeStorages }}
              </h5>
              <small class="text-muted">Verfügbar</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="PercentIcon"
                  size="24"
                  class="text-info"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ utilizationPercentage }}%
              </h5>
              <small class="text-muted">Auslastung</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Storage Heatmap -->
    <b-row class="mb-1">
      <b-col cols="12">
        <StorageHeatMap />
      </b-col>
    </b-row>

    <!-- Location Performance Chart -->
    <b-row class="mb-1">
      <b-col cols="12">
        <LocationPerformanceChart />
      </b-col>
    </b-row>

    <!-- Storage Utilization Trends -->
    <b-row class="mb-1">
      <b-col cols="12">
        <StorageUtilizationChart />
      </b-col>
    </b-row>
    
    <!-- Location Overview -->
    <b-row class="mb-1">
      <b-col cols="12">
        <b-card no-body>
          <b-card-header>
            <h4 class="mb-0">
              Standort Übersicht
            </h4>
            <small class="text-muted">Alle Standorte im Überblick</small>
          </b-card-header>
          <b-card-body>
            <div v-if="locations.length > 0">
              <div 
                v-for="location in locations" 
                :key="location.id"
                class="location-item d-flex justify-content-between align-items-center mb-3 p-3 border rounded"
              >
                <div>
                  <h6 class="mb-1">
                    {{ location.name || `Standort ${location.id}` }}
                  </h6>
                  <small class="text-muted">{{ getLocationStorageCount(location.id) }} Lager</small>
                </div>
                <div class="text-right">
                  <div class="location-utilization">
                    <span class="h6 mb-0">{{ getLocationUtilization(location.id) }}%</span>
                    <div
                      class="progress mt-1"
                      style="height: 4px;"
                    >
                      <div 
                        class="progress-bar"
                        :style="{ width: `${getLocationUtilization(location.id)}%` }"
                        :class="getUtilizationClass(getLocationUtilization(location.id))"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-center py-4"
            >
              <feather-icon
                icon="MapPinIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted">
                Keine Standorte gefunden
              </p>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BCard, BCardBody, BCardHeader, BRow, BCol, BButton } from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import businessMetrics from '@/helper/businessMetrics'
import StorageHeatMap from './StorageHeatMap.vue'
import LocationPerformanceChart from './LocationPerformanceChart.vue'
import StorageUtilizationChart from './StorageUtilizationChart.vue'

export default {
  name: 'StorageDashboard',
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BRow,
    BCol,
    BButton,
    FeatherIcon,
    StorageHeatMap,
    LocationPerformanceChart,
    StorageUtilizationChart,
  },
  setup() {
    const {
      getCountOfAllStorages,
      getFreeStorages,
      getRentedStorages,
    } = businessMetrics()
    
    return {
      getCountOfAllStorages,
      getFreeStorages,
      getRentedStorages,
    }
  },
  data() {
    return {
      totalStorages: 0,
      rentedStorages: 0,
      freeStorages: 0,
      locations: [],
      storages: [],
    }
  },
  computed: {
    utilizationPercentage() {
      if (this.totalStorages === 0) return 0
      return Math.round((this.rentedStorages / this.totalStorages) * 100)
    },
  },
  mounted() {
    this.loadStorageMetrics()
    this.loadLocationData()
  },
  methods: {
    loadStorageMetrics() {
      try {
        this.totalStorages = this.getCountOfAllStorages()
        this.rentedStorages = this.getRentedStorages()
        this.freeStorages = this.getFreeStorages()
      } catch (error) {
        console.error('Error loading storage metrics:', error)
      }
    },
    
    loadLocationData() {
      try {
        if (this.$store && this.$store.getters) {
          this.locations = this.$store.getters.locationsEntries || []
          this.storages = this.$store.getters.storageEntries || []
          
          console.log('StorageDashboard - Standort Übersicht Debug:')
          console.log('Locations loaded:', this.locations.length)
          console.log('Storages loaded:', this.storages.length)
          console.log('Order Items available:', this.$store.getters.orderItemsEntries?.length || 0)
          
          if (this.locations.length > 0) {
            console.log('Sample location:', this.locations[0])
          }
          if (this.storages.length > 0) {
            console.log('Sample storage:', this.storages[0])
          }
        }
      } catch (error) {
        console.error('Error loading location data:', error)
      }
    },
    
    getLocationStorageCount(locationId) {
      return this.storages.filter(storage => storage.location && storage.location.id === locationId).length
    },
    
    getLocationUtilization(locationId) {
      const locationStorages = this.storages.filter(storage => storage.location && storage.location.id === locationId)
      if (locationStorages.length === 0) return 0
      
      // Get order items from store and check which storages are rented
      const orderItems = this.$store.getters.orderItemsEntries || []
      const storageIds = locationStorages.map(storage => storage.id)
      
      const rentedStorageIds = new Set()
      orderItems.forEach(orderItem => {
        // Check if this order item is for a storage in this location and is currently active
        const isForThisLocation = storageIds.includes(orderItem.storage_id) 
                                  || (orderItem.storage && storageIds.includes(orderItem.storage.id))
        const isActive = !orderItem.moveOutDate
        
        if (isForThisLocation && isActive) {
          rentedStorageIds.add(orderItem.storage_id || orderItem.storage?.id)
        }
      })
      
      return Math.round((rentedStorageIds.size / locationStorages.length) * 100)
    },
    
    getUtilizationClass(percentage) {
      if (percentage >= 90) return 'bg-success'
      if (percentage >= 70) return 'bg-info'
      if (percentage >= 50) return 'bg-warning'
      return 'bg-danger'
    },
  },
}
</script>

<style scoped>

.avatar-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 123, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon .text-success {
  color: #28a745 !important;
}

.avatar-icon .text-primary {
  color: #007bff !important;
}

.avatar-icon .text-info {
  color: #17a2b8 !important;
}

.avatar-icon .text-warning {
  color: #ffc107 !important;
}

.location-item {
  transition: all 0.2s ease;
}

.location-item:hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.location-utilization {
  min-width: 80px;
}

@media (max-width: 768px) {
  .location-item {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .location-utilization {
    width: 100%;
    margin-top: 8px;
  }
}
</style>
